import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuiz } from '../context/QuizContext';
import { quizQuestions } from '../data/questions';
import { ProgressBar } from '../components/ui/ProgressBar';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export function Quiz() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const { answers, updateAnswer } = useQuiz();
  const navigate = useNavigate();

  const currentQuestion = quizQuestions[currentIndex];
  const isLastQuestion = currentIndex === quizQuestions.length - 1;

  // Jump to first unanswered or current if answers exist
  useEffect(() => {
    if (Object.keys(answers).length > 0 && currentIndex === 0) {
      // Find highest answered index
      let highest = 0;
      for (let i = 0; i < quizQuestions.length; i++) {
        if (answers[quizQuestions[i].id]) highest = i;
      }
      if (highest < quizQuestions.length - 1) {
        setCurrentIndex(highest + 1);
      }
    }
  }, []);

  const handleOptionSelect = (optionId) => {
    updateAnswer(currentQuestion.id, optionId);
    
    setTimeout(() => {
      if (isLastQuestion) {
        navigate('/capture');
      } else {
        setDirection(1);
        setCurrentIndex(prev => prev + 1);
      }
    }, 400); // Small delay to show selection
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    } else {
      navigate('/');
    }
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0
    })
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center pt-12 sm:pt-24 px-4">
      <div className="w-full max-w-2xl">
        
        <button 
          onClick={handleBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 mb-8 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2" />
          Back
        </button>

        <ProgressBar current={currentIndex + 1} total={quizQuestions.length} />

        <div className="mt-12 relative min-h-[400px]">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }}
              className="absolute w-full"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {currentQuestion.question}
              </h2>
              
              {currentQuestion.subtext && (
                <p className="text-gray-500 mb-6">{currentQuestion.subtext}</p>
              )}

              <div className="space-y-3 mt-8">
                {currentQuestion.options.map((option) => {
                  const isSelected = answers[currentQuestion.id] === option.id;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(option.id)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between group
                        ${isSelected 
                          ? 'border-brand-green bg-brand-green/5 ring-1 ring-brand-green' 
                          : 'border-gray-200 bg-white hover:border-brand-green hover:shadow-sm'
                        }`}
                    >
                      <span className={`font-medium ${isSelected ? 'text-brand-green' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                        ${isSelected ? 'border-brand-green' : 'border-gray-300 group-hover:border-brand-green'}`}
                      >
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-brand-green" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {answers[currentQuestion.id] && currentQuestion.educationalNote && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 bg-blue-50 text-blue-800 rounded-lg text-sm flex items-start"
                >
                  <SafeIcon icon={FiIcons.FiInfo} className="mt-0.5 mr-2 flex-shrink-0" />
                  <p>{currentQuestion.educationalNote}</p>
                </motion.div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
}